import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { LanguageSwitcher } from '../../components/language/LanguageSwitcher'
import { getSupabaseClient } from '../../lib/supabase'
import { useAuth } from '../auth/useAuth'
import { I18nContext, type Locale } from './i18n-context'
import { observeTranslations, translateUiText } from './translations'
import { normalizeAppLocale, traceAppLocale } from '../../../supabase/functions/_shared/app-locale'

const storageKey = 'propertyready.locale'
const userStorageKey = (userId: string) => `${storageKey}.${userId}`

function initialLocale(): Locale {
  const stored = window.localStorage.getItem(storageKey)
  const resolved = normalizeAppLocale(stored, window.navigator.language)
  traceAppLocale('i18n.initial', stored ?? window.navigator.language, resolved, import.meta.env.DEV)
  return resolved
}

export function I18nProvider({ children }: PropsWithChildren) {
  const { user } = useAuth()
  const userId = user?.id
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  const selectionVersion = useRef(0)
  const activeUserId = useRef(userId)
  const writeQueue = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => { activeUserId.current = userId }, [userId])

  useEffect(() => {
    if (!userId) return
    let active = true
    const cached = window.localStorage.getItem(userStorageKey(userId))
    const requestVersion = selectionVersion.current
    if (cached) {
      const normalizedCached = normalizeAppLocale(cached, window.localStorage.getItem(storageKey))
      void Promise.resolve().then(() => {
        if (active && activeUserId.current === userId && selectionVersion.current === requestVersion) setLocaleState(normalizedCached)
      })
    }
    void getSupabaseClient()
      .from('profiles')
      .select('preferred_language')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active || error || activeUserId.current !== userId || selectionVersion.current !== requestVersion) return
        const preferred = data?.preferred_language
        if (typeof preferred === 'string' && preferred.trim()) {
          const normalizedPreferred = normalizeAppLocale(preferred, window.localStorage.getItem(storageKey))
          traceAppLocale('i18n.profile', preferred, normalizedPreferred, import.meta.env.DEV)
          setLocaleState(normalizedPreferred)
          window.localStorage.setItem(storageKey, normalizedPreferred)
          window.localStorage.setItem(userStorageKey(userId), normalizedPreferred)
        }
      })
    return () => { active = false }
  }, [userId])

  const setLocale = useCallback((next: Locale) => {
    selectionVersion.current += 1
    setLocaleState(next)
    window.localStorage.setItem(storageKey, next)
    document.documentElement.lang = next
    if (user) {
      window.localStorage.setItem(userStorageKey(user.id), next)
      const targetUserId = user.id
      writeQueue.current = writeQueue.current.catch(() => undefined).then(async () => {
        const { error } = await getSupabaseClient()
          .from('profiles')
          .update({ preferred_language: next, updated_at: new Date().toISOString() })
          .eq('id', targetUserId)
        if (error) console.error('Unable to save the language preference.', { code: error.code, userId: targetUserId })
      })
    }
  }, [user])

  useEffect(() => {
    document.documentElement.lang = locale
    window.localStorage.setItem(storageKey, locale)
    return observeTranslations(locale)
  }, [locale])

  const value = useMemo(() => ({
    locale,
    setLocale,
    t: (text: string) => translateUiText(text, locale),
  }), [locale, setLocale])

  return (
    <I18nContext.Provider value={value}>
      {!user && <LanguageSwitcher className="public-language-switcher" />}
      {children}
    </I18nContext.Provider>
  )
}
